import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import MusicEmbed from "../components/MusicEmbed";
import { formatGNF, FALLBACK_CATEGORIES, UNLOCK_FEE_GNF } from "../constants/mcevents";
import { useAuth } from "../contexts/AuthContext";
import { useContent } from "../contexts/ContentContext";
import { MapPin, Calendar as CalendarIcon, Languages, ShieldCheck, X, Loader2, Youtube, Instagram, Facebook, Globe, Phone, Lock, Briefcase, Map, FileText, BadgeCheck } from "lucide-react";
import { WhatsAppIcon, TikTokIcon } from "../components/SocialIcons";
import { Calendar } from "../components/ui/calendar";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { fileUrl } from "../components/ImageUpload";

const FALLBACK = "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=1200&q=80";

function resolveImg(v) {
  if (!v) return null;
  if (v.startsWith("http") || v.startsWith("/")) return v;
  return fileUrl(v);
}

export default function ArtistProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useContent();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBooking, setShowBooking] = useState(false);

  // booking form state
  const [eventType, setEventType] = useState("Mariage");
  const [eventDate, setEventDate] = useState("");
  const [location, setLocation] = useState("");
  const [duration, setDuration] = useState(2);
  const [budget, setBudget] = useState(0);
  const [message, setMessage] = useState("");
  const [provider, setProvider] = useState("orange_money");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/artists/${id}`).then((r) => { setArtist(r.data.artist); setBudget(r.data.artist.base_price_gnf || 0); }).catch(() => setArtist(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="container-mc py-20 text-center font-mono text-sm uppercase tracking-wider text-muted-foreground">Chargement…</div>;
  if (!artist) return <div className="container-mc py-20 text-center">Artiste introuvable. <Link to="/explore" className="underline">Retour</Link></div>;

  const catLabel = FALLBACK_CATEGORIES.find((c) => c.id === artist.category)?.label || artist.category;
  const cover = resolveImg(artist.cover_image) || resolveImg(artist.photos?.[0]) || FALLBACK;

  const openBooking = () => {
    if (!user) { navigate("/login", { state: { from: { pathname: `/artists/${id}` } } }); return; }
    if (user.role !== "client") { toast.error("Seuls les clients peuvent solliciter un artiste."); return; }
    setShowBooking(true);
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        artist_id: artist.id,
        event_type: eventType,
        event_date: eventDate,
        location,
        duration_hours: Number(duration),
        budget_gnf: Number(budget),
        message,
        payment_provider: provider,
        payment_phone: phone,
      });
      if (data.payment_url) {
        toast.info("Redirection vers le paiement sécurisé FedaPay…");
        window.location.href = data.payment_url;
        return;
      }
      toast.success(`Paiement réussi · Réf ${data.payment.reference}`);
      setShowBooking(false);
      if (data.conversation_id) navigate(`/messages/${data.conversation_id}`);
      else navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-mc py-10" data-testid="artist-profile-page">
      <Link to="/explore" className="text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground">← Retour</Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-6">
        {/* LEFT: gallery + bio */}
        <div className="lg:col-span-7">
          <div className="card-mc overflow-hidden aspect-[4/3] bg-muted">
            <img src={cover} alt={artist.stage_name} className="w-full h-full object-cover" />
          </div>

          {artist.photos?.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-3">
              {artist.photos.slice(0, 8).map((p, i) => (
                <div key={i} className="card-mc overflow-hidden aspect-square bg-muted">
                  <img src={resolveImg(p)} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          <div className="mt-10 flex items-start gap-5">
            {artist.profile_photo && (
              <img src={resolveImg(artist.profile_photo)} alt={artist.stage_name}
                className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-[0_6px_20px_rgba(13,27,42,0.18)] ring-1 ring-border shrink-0"
                data-testid="artist-avatar" />
            )}
            <div className="min-w-0">
              <p className="overline mb-2">{catLabel}</p>
              <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter flex items-center gap-2.5">
                {artist.stage_name}
                {artist.verified && (
                  <span className="inline-flex items-center gap-1 bg-[hsl(var(--primary))] text-white text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full align-middle" data-testid="artist-verified-badge">
                    <BadgeCheck className="w-3.5 h-3.5" /> Vérifié
                  </span>
                )}
              </h1>
              <div className="flex flex-wrap gap-5 mt-3 text-sm text-muted-foreground font-mono uppercase tracking-wider">
                <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {artist.city}{artist.country ? `, ${artist.country}` : ""}</span>
                {artist.years_experience > 0 && <span className="flex items-center gap-1.5"><CalendarIcon className="w-3.5 h-3.5" /> {artist.years_experience} ans d'expérience</span>}
                {artist.languages?.length > 0 && <span className="flex items-center gap-1.5"><Languages className="w-3.5 h-3.5" /> {artist.languages.join(", ")}</span>}
              </div>
              {/* Réseaux sociaux */}
              {(artist.youtube || artist.tiktok || artist.instagram || artist.facebook || artist.website) && (
                <div className="flex flex-wrap gap-2 mt-4" data-testid="artist-socials">
                  {[
                    { url: artist.youtube, Icon: Youtube, label: "YouTube", color: "#FF0000", tid: "youtube" },
                    { url: artist.tiktok, Icon: TikTokIcon, label: "TikTok", color: "#0D1B2A", tid: "tiktok" },
                    { url: artist.instagram, Icon: Instagram, label: "Instagram", color: "#E4405F", tid: "instagram" },
                    { url: artist.facebook, Icon: Facebook, label: "Facebook", color: "#1877F2", tid: "facebook" },
                    { url: artist.website, Icon: Globe, label: "Site web", color: "#2563EB", tid: "website" },
                  ].filter((s) => s.url).map(({ url, Icon, label, color, tid }) => (
                    <a key={tid} href={url.startsWith("http") ? url : `https://${url}`} target="_blank" rel="noreferrer"
                      data-testid={`artist-social-${tid}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-border rounded-full bg-white hover:border-[hsl(var(--primary))]/50 hover:-translate-y-0.5 transition-all duration-200">
                      <Icon className="w-3.5 h-3.5" style={{ color }} /> {label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 card-mc p-6">
            <p className="overline mb-3">Biographie</p>
            <p className="text-base leading-relaxed whitespace-pre-line" data-testid="artist-bio">{artist.bio || "Aucune biographie renseignée."}</p>
          </div>

          {/* Prestations & zone de déplacement */}
          {(artist.services?.length > 0 || artist.travel_zone) && (
            <div className="mt-8 card-mc p-6" data-testid="artist-services-block">
              <p className="overline mb-4 flex items-center gap-2"><Briefcase className="w-4 h-4" /> Prestations</p>
              {artist.services?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {artist.services.map((s, i) => (
                    <span key={i} className="inline-flex items-center bg-[hsl(var(--primary))]/8 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/20 px-3 py-1.5 text-xs font-bold rounded-full">{s}</span>
                  ))}
                </div>
              )}
              {artist.travel_zone && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Map className="w-4 h-4 text-[hsl(var(--secondary))]" />
                  <span><strong className="text-foreground">Zone de déplacement :</strong> {artist.travel_zone}</span>
                </p>
              )}
            </div>
          )}

          {/* Disponibilités */}
          {(artist.unavailable_dates?.length > 0 || artist.availability_note) && (
            <div className="mt-8 card-mc p-6" data-testid="artist-availability-block">
              <p className="overline mb-3 flex items-center gap-2"><CalendarIcon className="w-4 h-4" /> Disponibilités</p>
              {artist.availability_note && <p className="text-sm text-muted-foreground mb-4">{artist.availability_note}</p>}
              {artist.unavailable_dates?.length > 0 && (
                <div className="flex flex-col sm:flex-row gap-5 items-start">
                  <Calendar
                    locale={fr}
                    defaultMonth={(() => {
                      const today = new Date().toISOString().slice(0, 10);
                      const next = [...artist.unavailable_dates].sort().find((d) => d >= today);
                      return next ? new Date(`${next}T00:00:00`) : new Date();
                    })()}
                    modifiers={{ unavailable: artist.unavailable_dates.map((d) => new Date(`${d}T00:00:00`)) }}
                    modifiersClassNames={{ unavailable: "bg-destructive/15 text-destructive line-through rounded-md" }}
                    className="border border-border rounded-xl"
                  />
                  <p className="text-xs text-muted-foreground flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded bg-destructive/20 border border-destructive/40" />
                    Dates barrées = artiste indisponible
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Conditions de réservation */}
          {artist.booking_conditions && (
            <div className="mt-8 card-mc p-6" data-testid="artist-conditions-block">
              <p className="overline mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Conditions de réservation</p>
              <p className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground">{artist.booking_conditions}</p>
            </div>
          )}

          {/* Music samples */}
          <div className="mt-8">
            <p className="overline mb-3">Extraits musicaux</p>
            {artist.music_links?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="music-extracts">
                {artist.music_links.map((u, i) => <MusicEmbed key={i} url={u} idx={i} />)}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">L'artiste n'a pas encore publié d'extraits.</p>
            )}
          </div>

          {/* Vidéos */}
          {artist.videos?.length > 0 && (
            <div className="mt-8" data-testid="artist-videos-block">
              <p className="overline mb-3">Vidéos</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {artist.videos.map((u, i) => <MusicEmbed key={i} url={u} idx={`video-${i}`} />)}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: sticky booking box */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-24 card-mc p-6 md:p-8" data-testid="booking-box">
            <p className="overline">Solliciter cet artiste</p>
            <div className="mt-4 flex items-baseline gap-2 flex-wrap" data-testid="artist-price-range">
              <span className="font-display font-black text-3xl sm:text-4xl tracking-tighter">
                {formatGNF(artist.base_price_gnf)}{artist.price_max_gnf > 0 ? ` – ${formatGNF(artist.price_max_gnf)}` : ""}
              </span>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{artist.price_max_gnf > 0 ? "fourchette de prix" : "à partir de"}</span>
            </div>

            {/* Contact direct — visible après mise en relation payée */}
            <div className="mt-5" data-testid="artist-contact-block">
              {artist.contact_visible && (artist.phone_public || artist.whatsapp) ? (
                <div className="border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5 rounded-xl p-4 space-y-2.5">
                  <p className="text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--accent))] flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5" /> Contact direct débloqué
                  </p>
                  {artist.phone_public && (
                    <a href={`tel:${artist.phone_public.replace(/\s/g, "")}`} data-testid="artist-phone-link"
                      className="flex items-center gap-2 text-sm font-bold hover:text-[hsl(var(--primary))] transition-colors">
                      <Phone className="w-4 h-4 text-[hsl(var(--primary))]" /> {artist.phone_public}
                    </a>
                  )}
                  {artist.whatsapp && (
                    <a href={`https://wa.me/${artist.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" data-testid="artist-whatsapp-link"
                      className="inline-flex items-center gap-2 bg-[#25D366] text-white text-sm font-bold px-4 py-2 rounded-full hover:bg-[#1DA851] transition-colors">
                      <WhatsAppIcon className="w-4 h-4" /> Discuter sur WhatsApp
                    </a>
                  )}
                </div>
              ) : (
                <div className="border border-dashed border-border rounded-xl p-4 flex items-center gap-3 text-muted-foreground">
                  <Lock className="w-4 h-4 text-[hsl(var(--secondary))] shrink-0" />
                  <p className="text-xs">Téléphone et WhatsApp de l'artiste visibles après la mise en relation payée.</p>
                </div>
              )}
            </div>

            <div className="mt-6 border border-dashed border-border p-4 bg-[hsl(var(--muted))]/40">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-4 h-4 text-[hsl(var(--accent))]" />
                <p className="text-[11px] font-mono uppercase tracking-wider">Frais de mise en relation</p>
              </div>
              <p className="font-display font-black text-2xl">{formatGNF(UNLOCK_FEE_GNF)}</p>
              <p className="text-xs text-muted-foreground mt-1">Payable une fois par <span className="pill-orange ml-1">ORANGE MONEY</span> <span className="pill-mtn ml-1">MTN MoMo</span></p>
            </div>

            <button onClick={openBooking} className="btn-primary w-full mt-6" data-testid="open-booking-btn">
              Solliciter & Payer {formatGNF(UNLOCK_FEE_GNF)}
            </button>

            <ul className="mt-6 space-y-2 text-xs text-muted-foreground">
              {t("booking.unlock_features", "Écoute libre des extraits avant paiement\nContact direct après mise en relation\nContrat sécurisé avec validation à 2 parties").split("\n").filter(Boolean).map((line, i) => (
                <li key={i}>✓ {line}</li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      {/* BOOKING MODAL */}
      {showBooking && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" data-testid="booking-modal">
          <div className="bg-card w-full max-w-lg card-mc border-foreground max-h-[95vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card">
              <div>
                <p className="overline">Nouvelle sollicitation</p>
                <h3 className="font-display font-black text-2xl tracking-tighter">{artist.stage_name}</h3>
              </div>
              <button data-testid="close-booking-modal" onClick={() => setShowBooking(false)}><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={submitBooking} className="p-5 space-y-4">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Type d'événement</label>
                <select data-testid="event-type" value={eventType} onChange={(e) => setEventType(e.target.value)} className="mt-1 w-full border border-border px-3 py-2 bg-card rounded-sm">
                  {["Mariage", "Concert", "Soirée privée", "Entreprise", "Lancement produit", "Festival", "Anniversaire", "Autre"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Date</label>
                  <input data-testid="event-date" required type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} className="mt-1 w-full border border-border px-3 py-2 bg-card rounded-sm" />
                </div>
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Durée (h)</label>
                  <input data-testid="event-duration" required type="number" min="1" value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-1 w-full border border-border px-3 py-2 bg-card rounded-sm" />
                </div>
              </div>

              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Lieu</label>
                <input data-testid="event-location" required value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ex: Conakry, Kaloum" className="mt-1 w-full border border-border px-3 py-2 bg-card rounded-sm" />
              </div>

              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Budget proposé (GNF)</label>
                <input data-testid="event-budget" required type="number" min="0" value={budget} onChange={(e) => setBudget(e.target.value)} className="mt-1 w-full border border-border px-3 py-2 bg-card rounded-sm font-mono" />
              </div>

              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Message à l'artiste</label>
                <textarea data-testid="event-message" required value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="mt-1 w-full border border-border px-3 py-2 bg-card rounded-sm" placeholder="Décrivez votre événement, vos attentes..." />
              </div>

              <div className="border-t border-border pt-4 mt-2">
                <p className="overline mb-3">Paiement · {formatGNF(UNLOCK_FEE_GNF)}</p>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button type="button" data-testid="provider-orange" onClick={() => setProvider("orange_money")} className={`p-3 border rounded-sm flex items-center gap-2 ${provider === "orange_money" ? "border-foreground bg-foreground text-background" : "border-border"}`}>
                    <span className="pill-orange">OM</span>
                    <span className="text-sm font-bold">Orange Money</span>
                  </button>
                  <button type="button" data-testid="provider-mtn" onClick={() => setProvider("mtn_momo")} className={`p-3 border rounded-sm flex items-center gap-2 ${provider === "mtn_momo" ? "border-foreground bg-foreground text-background" : "border-border"}`}>
                    <span className="pill-mtn">MoMo</span>
                    <span className="text-sm font-bold">MTN MoMo</span>
                  </button>
                </div>
                <input data-testid="payment-phone" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Numéro de téléphone (ex: 622 00 00 00)" className="w-full border border-border px-3 py-2 bg-card rounded-sm font-mono" />
                <p className="text-[10px] text-muted-foreground mt-2 italic">{t("booking.reassurance", "Vous recevrez une confirmation par email après le paiement.")}</p>
              </div>

              <button type="submit" disabled={submitting} data-testid="submit-booking" className="btn-primary w-full">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Confirmer & Payer {formatGNF(UNLOCK_FEE_GNF)}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
